import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Tooltip,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MergeTagPickerPopover } from "./pickers/MergeTagPickerPopover";
import { OfferPickerPopover } from "./pickers/OfferPickerPopover";
import { SingleLinePlugin } from "./plugins/SingleLinePlugin";
import { ValueSyncPlugin } from "./plugins/ValueSyncPlugin";
import { resolveMergeTags } from "./resolveMergeTags";
import type { InlineLexicalInputProps } from "./types";
import { MergeTagAutoDetectPlugin } from "../extensions/mergeTags/editor/MergeTagAutoDetectPlugin";
import { MergeTagNode } from "../extensions/mergeTags/editor/MergeTagNode";
import { MergeTagPlugin } from "../extensions/mergeTags/editor/mergeTagPlugin";
import { MergeTagTypeaheadPlugin } from "../extensions/mergeTags/editor/MergeTagTypeaheadPlugin";
import { useConnectorMetadataMergeTagCategories } from "../extensions/mergeTags/useConnectorMergeTags";
import { OfferAutoDetectPlugin } from "../extensions/offers/editor/OfferAutoDetectPlugin";
import { OfferCommandPlugin } from "../extensions/offers/editor/OfferCommandPlugin";
import {
  OfferLabelContext,
  OfferListContext,
  type OfferLabelResolver,
} from "../extensions/offers/editor/offerLabelContext";
import {
  OfferNode,
  type OfferDefinition,
} from "../extensions/offers/editor/OfferNode";
import type { MergeTagCategory } from "@/services/mergeTags";
import type { Klass, LexicalNode } from "lexical";

import { CheckIcon as Check, TagIcon } from "@/icons";
import { OfferCodeIcon } from "@/orion/OrionIcons/OrionOfferCodeIcon";
// OfferNode is inert without offer plugins, so registering it unconditionally
// is safe and avoids re-keying the config on the `offers` prop (LexicalComposer
// reads `initialConfig` only once at mount, so a memo on it never re-registers).
const EDITOR_NODES: Klass<LexicalNode>[] = [MergeTagNode, OfferNode];

const EMPTY_OFFERS: OfferDefinition[] = [];

const INITIAL_CONFIG = {
  namespace: "InlineLexicalInput",
  nodes: EDITOR_NODES,
  onError: (err: Error) => console.error("InlineLexicalInput:", err),
  theme: {
    paragraph: "inline-lexical-paragraph",
  },
};

// LexicalComposer reads `editable` off InitialConfig only once at mount, so
// flipping the prop alone won't update the editor. Sync it via setEditable.
function EditableSync({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);
  return null;
}

export function InlineLexicalInput({
  value,
  onChange,
  onSave,
  saveLabel,
  saveDisabled = false,
  savedValue,
  surface,
  allowedTags,
  hiddenTags,
  extraCategories: extraCategoriesProp,
  offers,
  placeholder,
  startAdornment,
  label,
  error = false,
  helperText,
  editable = true,
  onBlur,
}: InlineLexicalInputProps) {
  const theme = useTheme();
  const connectorCategories = useConnectorMetadataMergeTagCategories();
  const extraCategories: MergeTagCategory[] | undefined =
    extraCategoriesProp ?? (allowedTags ? undefined : connectorCategories);

  // One resolved source of truth for which tags the picker offers, the typeahead
  // suggests, and auto-detect will chip — so they can never diverge.
  const { categories, allowedIdentifiers } = useMemo(
    () =>
      resolveMergeTags({ surface, allowedTags, hiddenTags, extraCategories }),
    [surface, allowedTags, hiddenTags, extraCategories],
  );

  const [mergeTagAnchor, setMergeTagAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [offerAnchor, setOfferAnchor] = useState<HTMLElement | null>(null);
  // True while the merge tag typeahead menu is open, so SingleLinePlugin yields
  // the Enter key to the menu's select handler instead of swallowing it.
  const typeaheadOpenRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);
  // An external save that updates `savedValue` clears the Save button. Such as broadcast drafts
  const isDirtyEffective =
    savedValue !== undefined ? value !== savedValue : isDirty;
  const [isSaving, setIsSaving] = useState(false);
  const savedValueRef = useRef(value);
  // The most recent value we emitted through onChange. Used to tell an external
  // value change (reset, server refresh) apart from the controlled parent
  // echoing back the user's own edit.
  const lastEmittedRef = useRef(value);

  // Resolve chip text from the live offer list at render time (mirroring the
  // legacy TextFieldWithHighlightedTags), so a chip created before its offer
  // data arrived re-resolves to the offer's preview once it loads.
  const resolveOfferLabel = useMemo<OfferLabelResolver | null>(
    () => (offers ? (slug) => offers.lookup(slug)?.preview : null),
    [offers],
  );

  const handleChange = useCallback(
    (next: string) => {
      lastEmittedRef.current = next;
      setIsDirty(next !== savedValueRef.current);
      onChange(next);
    },
    [onChange],
  );

  const handleSave = useCallback(async () => {
    if (!onSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(value);
      savedValueRef.current = value;
      setIsDirty(false);
    } catch {
      // Save was cancelled or failed — keep the field dirty so the user can retry.
    } finally {
      setIsSaving(false);
    }
  }, [onSave, value, isSaving]);

  // Reconcile when `value` changes. A change that echoes our own last edit keeps
  // the current dirty state; a change from any other source becomes the new
  // saved baseline, so the field is no longer dirty against it (even if there
  // were unsaved edits — the external value now wins).
  const lastValuePropRef = useRef(value);
  if (value !== lastValuePropRef.current) {
    lastValuePropRef.current = value;
    if (value !== lastEmittedRef.current) {
      savedValueRef.current = value;
      lastEmittedRef.current = value;
      setIsDirty(false);
    }
  }

  return (
    <FormControl fullWidth error={error}>
      {label && (
        <InputLabel
          shrink
          sx={{ position: "static", transform: "none", mb: 1 }}
        >
          {label}
        </InputLabel>
      )}
      <OfferLabelContext.Provider value={resolveOfferLabel}>
        <OfferListContext.Provider value={offers?.list ?? EMPTY_OFFERS}>
          <LexicalComposer initialConfig={INITIAL_CONFIG}>
            <EditableSync editable={editable} />
            <Box
              onBlur={(e) => {
                if (
                  onBlur &&
                  !e.currentTarget.contains(e.relatedTarget as Node | null)
                ) {
                  onBlur();
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                border: 1,
                borderColor: error ? "error.main" : "divider",
                borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                transition: "border-color 0.15s",
                "&:focus-within": {
                  borderColor: error ? "error.main" : "primary.main",
                  borderWidth: 2,
                  margin: "-1px",
                },
                backgroundColor: "background.paper",
              }}
            >
              {startAdornment && (
                <Box sx={{ pl: 2, display: "flex", alignItems: "center" }}>
                  {startAdornment}
                </Box>
              )}

              <Box
                sx={{
                  flex: 1,
                  position: "relative",
                  minWidth: 0,
                  "& .inline-lexical-paragraph": {
                    margin: 0,
                    padding: 0,
                    whiteSpace: "pre",
                    width: "max-content",
                    minWidth: "100%",
                  },
                  "& [data-lexical-editor]": {
                    outline: "none",
                    // `pre` not `nowrap`: nowrap collapses an edge space to a
                    // zero-width (0,0,0,0) rect, which makes Lexical scroll-jump.
                    whiteSpace: "pre",
                    overflowX: "auto",
                    overflowY: "hidden",
                    padding: "8.5px 14px",
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: "1.4375em",
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.text.primary,
                  },
                  "& .inline-lexical-placeholder": {
                    color: theme.palette.text.secondary,
                    opacity: 0.6,
                    position: "absolute",
                    top: "8.5px",
                    left: "14px",
                    pointerEvents: "none",
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: "1.4375em",
                    fontFamily: theme.typography.fontFamily,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "calc(100% - 28px)",
                  },
                }}
              >
                <PlainTextPlugin
                  contentEditable={
                    <ContentEditable aria-label={label ?? "Inline input"} />
                  }
                  placeholder={
                    <div className="inline-lexical-placeholder">
                      {placeholder}
                    </div>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
              </Box>

              {editable && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexShrink: 0,
                    borderLeft: 1,
                    borderColor: "divider",
                    backgroundColor: "background.paper-elevation-1",
                    px: 1,
                    alignSelf: "stretch",
                  }}
                >
                  <Tooltip title="Insert merge tag">
                    <IconButton
                      size="small"
                      onClick={(e) => setMergeTagAnchor(e.currentTarget)}
                      aria-label="Insert merge tag"
                    >
                      <TagIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  {offers && (
                    <Tooltip title="Insert offer">
                      <IconButton
                        size="small"
                        onClick={(e) => setOfferAnchor(e.currentTarget)}
                        aria-label="Insert offer"
                      >
                        <OfferCodeIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <AnimatePresence>
                    {onSave &&
                      (isDirtyEffective || isSaving) &&
                      (saveLabel ? (
                        <motion.div
                          key="save-labeled"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: "auto", opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <Button
                            size="small"
                            onClick={() => void handleSave()}
                            disabled={isSaving || saveDisabled}
                            color="success"
                            startIcon={
                              isSaving ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Check sx={{ fontSize: 18 }} />
                              )
                            }
                            sx={{
                              whiteSpace: "nowrap",
                              textTransform: "none",
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            }}
                            variant="contained"
                          >
                            {saveLabel}
                          </Button>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="save-icon"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: "auto", opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={() => void handleSave()}
                              disabled={isSaving || saveDisabled}
                              aria-label="Save"
                              color="success"
                            >
                              {isSaving ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <Check sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </Box>
              )}

              <MergeTagPickerPopover
                anchorEl={mergeTagAnchor}
                onClose={() => setMergeTagAnchor(null)}
                categories={categories}
              />

              {offers && (
                <OfferPickerPopover
                  anchorEl={offerAnchor}
                  onClose={() => setOfferAnchor(null)}
                  offers={offers.list}
                />
              )}
            </Box>

            {/* Behavioral plugins */}
            <HistoryPlugin />
            <SingleLinePlugin typeaheadOpenRef={typeaheadOpenRef} />

            {/* Auto-detect transforms must register before ValueSyncPlugin
            populates the editor, so patterns become chips on first render. */}
            <MergeTagAutoDetectPlugin allowedIdentifiers={allowedIdentifiers} />
            {offers && <OfferAutoDetectPlugin lookup={offers.lookup} />}

            <ValueSyncPlugin value={value} onChange={handleChange} />

            {/* Merge tag plugins (reused from TextComposerV2) */}
            <MergeTagPlugin />
            <MergeTagTypeaheadPlugin
              categories={categories}
              onOpenChange={(open) => {
                typeaheadOpenRef.current = open;
              }}
            />

            {/* Offer plugins (conditionally rendered) */}
            {offers && <OfferCommandPlugin />}
          </LexicalComposer>
        </OfferListContext.Provider>
      </OfferLabelContext.Provider>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
